import { Select as SelectPrimitive } from "@kobalte/core/select";
import { Check, ChevronsUpDown, Plus } from "@fleet/icons";
import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@comandero/shared";

/**
 * Option of a premium Select. `value` is the stable key persisted and
 * `label` the displayed text (and used by Kobalte's keyboard typeahead).
 */
interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * CTA rendered inside dropdown when Select has no options
 * (AWS style: an empty combo offers "Create X" / "Go to X" instead of a dead control
 * muerto).
 *
 * Pass `onSelect` to execute an action (open a creation dialog or
 * focus an input) or `href` to render a link (navigate to the screen
 * providing options). `can` (default `true`) gates the CTA by permission —
 * when `false`, empty dropdown falls back to "no options" message and no
 * creation affordance is shown. Omit `emptyAction` if the source is not
 * creable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true. */
  can?: boolean;
  /** Leading icon of the CTA. Default: a "+" glyph. */
  icon?: JSX.Element;
  /** Optional helper line above the CTA (e.g. "No categories yet"). */
  description?: string;
}

interface SelectProps {
  /** Opciones a renderizar. */
  options: SelectOption[];
  /** Controlled value (key). Use `""`/undefined for "no selection". */
  value?: string;
  /** Notifies new value; `""` when selection is cleared. */
  onChange?: (value: string) => void;
  /** Placeholder when no value (equivalent to native empty option). */
  placeholder?: string;
  /** aria-label / nombre accesible del control. */
  "aria-label"?: string;
  /** Trigger testid (preserves compat with prior native selects). */
  "data-testid"?: string;
  disabled?: boolean;
  class?: string;
  /** Minimum panel width; defaults to matching trigger. */
  contentClass?: string;
  /** Si true, el trigger ocupa todo el ancho disponible. */
  fluid?: boolean;
  /** Visual trigger size. */
  size?: "sm" | "md" | "lg";
  /**
   * Shown inside dropdown when `options` is empty. Allows jumping
   * to where options are created instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message in empty dropdown when no CTA (or no permission). */
  emptyLabel?: string;
}

const triggerSizes = {
  sm: "h-8 px-2 text-xs gap-1.5",
  md: "h-10 px-3 text-sm gap-2",
  lg: "h-12 px-4 text-base gap-2",
} as const;

/**
 * Premium Select based on `@kobalte/core` replacing native `<select>`.
 *
 * - Theme-safe: usa tokens del repo (accent/accent-foreground, border, popover…).
 * - Keyboard + typeahead + ARIA ready out of the box (Kobalte).
 * - Portalized panel animated ONLY in opacity (`.ui-select-content`), never in
 *   transform/scale: that would override @floating-ui and cause open flicker.
 *
 * Modeled as single-select over `string`. Empty selection represented
 * by `""` (or `undefined`): Kobalte uses `null` internally, so we map
 * at boundaries to avoid propagating `null` to rest of app.
 */
export function Select(props: SelectProps) {
  const [local] = splitProps(props, [
    "options",
    "value",
    "onChange",
    "placeholder",
    "aria-label",
    "data-testid",
    "disabled",
    "class",
    "contentClass",
    "fluid",
    "size",
    "emptyAction",
    "emptyLabel",
  ]);

  const size = () => local.size ?? "md";
  const isEmpty = () => local.options.length === 0;

  // Map controlled key to corresponding option (or null = no value).
  const selected = (): SelectOption | null =>
    local.options.find((o) => o.value === local.value) ?? null;

  return (
    <SelectPrimitive<SelectOption>
      options={local.options}
      optionValue="value"
      optionTextValue="label"
      optionDisabled="disabled"
      value={selected()}
      // Required single-select: re-clicking already selected option must NOT
      // unselect it (Kobalte toggle would emit null → coercion to "" →
      // empty field before submit). Value can be changed to another option,
      // but not cleared by re-clicking.
      disallowEmptySelection
      onChange={(option) => {
        // Kobalte ALSO emits an empty selection on its own — measured: it fires
        // `onChange` while the controlled `value` is not (yet) present in
        // `options` (a controlled Select whose options load asynchronously), and
        // again on every re-render. Those are synchronisation artefacts, not a
        // user choice: with `disallowEmptySelection` the UI cannot produce an
        // empty selection at all. Forwarding them silently wipes the value the
        // caller legitimately holds (e.g. /activity?session=… seeds the shift
        // filter and the first artefact emission resets it to "All activity"),
        // so only a real, different option is forwarded.
        const next = option?.value;
        if (next === undefined || next === (local.value ?? "")) {
          return;
        }
        local.onChange?.(next);
      }}
      disabled={local.disabled}
      placeholder={local.placeholder}
      gutter={6}
      sameWidth
      itemComponent={(itemProps) => (
        <SelectPrimitive.Item
          item={itemProps.item}
          data-testid="select-item"
          data-value={itemProps.item.rawValue.value}
          class={cn(
            "relative flex cursor-pointer select-none items-center gap-2 rounded-md py-2 pl-3 pr-8 text-sm outline-none",
            "transition-colors duration-150",
            "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
            "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          )}
        >
          <SelectPrimitive.ItemLabel>{itemProps.item.rawValue.label}</SelectPrimitive.ItemLabel>
          <SelectPrimitive.ItemIndicator class="absolute right-2 flex items-center">
            <Check class="h-4 w-4" />
          </SelectPrimitive.ItemIndicator>
        </SelectPrimitive.Item>
      )}
    >
      <SelectPrimitive.Trigger
        data-testid={local["data-testid"]}
        aria-label={local["aria-label"]}
        class={cn(
          "inline-flex items-center justify-between rounded-md border border-input bg-background",
          "text-foreground outline-none transition-colors duration-150",
          "hover:border-accent/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          "active:scale-[0.99] motion-reduce:active:scale-100",
          "disabled:cursor-not-allowed disabled:opacity-50",
          triggerSizes[size()],
          local.fluid ? "w-full" : "min-w-[8rem]",
          local.class,
        )}
      >
        <SelectPrimitive.Value<SelectOption> class="truncate text-left">
          {(state) => state.selectedOption()?.label}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon class="shrink-0 opacity-60 transition-transform duration-200 data-[expanded]:rotate-180">
          <ChevronsUpDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          class={cn(
            "ui-select-content z-[250] overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-2xl outline-none",
            local.contentClass,
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-72 overflow-y-auto outline-none" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive>
  );
}

/**
 * Content of empty Select dropdown. Renders `emptyAction` CTA
 * (creation button or nav link) when present and permitted,
 * otherwise a dimmed "no options" message.
 *
 * The CTA is reachable by keyboard (it is a native button/anchor, auto-focusable
 * inside portalized panel) and inherits opacity-only entrance of panel
 * (`ui-select-content`), so it respects `prefers-reduced-motion` via global
 * guard like rest of UI. NO transform added (would break no-flicker
 * panel no-flicker).
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-2.5 py-3 text-center text-sm text-muted-foreground">
          {/* La copia vive en el call site para que este primitivo siga
              i18n-clean; el em-dash neutro es el placeholder de último recurso
              cuando no se pasa label. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1">
          <Show when={action().description}>
            <p class="px-2.5 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}

/**
 * Small helper to build `SelectOption[]` from arbitrary data without
 * repeating `.map` in each feature.
 */
export function toSelectOptions<T>(
  items: readonly T[] | undefined,
  getValue: (item: T) => string | null | undefined,
  getLabel: (item: T) => string | null | undefined,
): SelectOption[] {
  return (items ?? []).map((item) => ({
    value: getValue(item) ?? "",
    label: getLabel(item) ?? "",
  }));
}
